import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect } from "react";

import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { isLocalAutoLogin } from "@/helpers/local-mock";
import { useBool } from "@/hooks/useBool";
import { RawAccessTokenProvider, SuperTokensAccessTokenProvider } from "@/providers/AccessTokenProvider";
import { useConfig } from "@/providers/config/useConfig";
import { StartupLoading } from "@/providers/StartupLoading";
import { SESSION_IDENTITY_QUERY_KEY } from "@/queries/authentication/queryOptions";
import { useTranslation } from "@/translations";

import { createLocalDevelopmentAccessToken } from "./createLocalDevelopmentAccessToken";
import { getSuperTokensInitializedAt, initializeSuperTokens, setSessionEventHandler } from "./superTokensClient";
import { isSignOutInProgress, useLogout } from "./useLogout";

const SessionExpiredContext = createContext<{ sessionExpired: boolean; onClose: () => void } | null>(null);

/** Renders the SDK notice in the active route's language rather than the anonymous bootstrap dictionary. */
export function SessionExpiredNotice(): React.ReactNode {
  const notice = useContext(SessionExpiredContext);

  return notice ? <SessionExpiredModal {...notice} /> : null;
}

export function SuperTokensProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  if (isLocalAutoLogin) {
    return (
      <RawAccessTokenProvider
        token={createLocalDevelopmentAccessToken(
          import.meta.env.VITE_LOCAL_DEV_USER_EMAIL,
          import.meta.env.VITE_LOCAL_DEV_USER_ID,
        )}
      >
        {children}
      </RawAccessTokenProvider>
    );
  }

  return <SuperTokensSessionProvider>{children}</SuperTokensSessionProvider>;
}

function SuperTokensSessionProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const logout = useLogout();
  const queryClient = useQueryClient();
  const authenticationServerRootUrl = useConfig("authenticationServerRootUrl");
  const newCoreApiRootUri = useConfig("newCoreApiRootUri");
  const [sessionExpired, sessionExpiredHandlers] = useBool();
  const [initialized, initializedHandlers] = useBool();

  useEffect(() => {
    initializeSuperTokens({ authenticationServerRootUrl, newCoreApiRootUri });
    initializedHandlers.setTrue();

    return setSessionEventHandler((event) => {
      if (["SESSION_CREATED", "REFRESH_SESSION", "ACCESS_TOKEN_PAYLOAD_UPDATED", "SIGN_OUT"].includes(event.action)) {
        void queryClient.invalidateQueries({ queryKey: SESSION_IDENTITY_QUERY_KEY });
      }
      if (event.action === "UNAUTHORISED") {
        if (isSignOutInProgress()) {
          return;
        }

        // SuperTokens also triggers this event when a page loads without a session.
        // Always clean up session data, but only show the notice for expired or revoked sessions.
        const initializedAt = getSuperTokensInitializedAt();
        if (
          event.sessionExpiredOrRevoked &&
          (!initializedAt || new Date().getTime() - initializedAt.getTime() > 5000)
        ) {
          sessionExpiredHandlers.setTrue();
        }

        void logout({ keepUrl: true });
      }
    });
  }, [
    authenticationServerRootUrl,
    initializedHandlers,
    logout,
    newCoreApiRootUri,
    sessionExpiredHandlers,
    queryClient,
  ]);

  if (!initialized) {
    return <StartupLoading />;
  }

  return (
    <SessionExpiredContext value={{ sessionExpired, onClose: sessionExpiredHandlers.setFalse }}>
      <SuperTokensAccessTokenProvider>{children}</SuperTokensAccessTokenProvider>
    </SessionExpiredContext>
  );
}

function SessionExpiredModal({
  sessionExpired,
  onClose,
}: {
  sessionExpired: boolean;
  onClose: () => void;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Modal.Root
      isOpened={sessionExpired}
      onOpenChange={(state) => {
        if (!state) {
          onClose();
        }
      }}
    >
      <Modal.Body>
        <article className="grid grid-cols-1 gap-y-2 px-8 py-4">
          <h2 className="max-w-sm justify-self-center text-heading-3">{t("component.session-expired-modal.title")}</h2>
          <p className="max-w-sm justify-self-center">{t("component.session-expired-modal.description")}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2">
            <Button onClick={onClose} fullWidth="untilSm" variant="soft">
              {t("component.session-expired-modal.log-in")}
            </Button>
          </div>
        </article>
      </Modal.Body>
    </Modal.Root>
  );
}
